import {flow, getEnv, IDisposer, Instance, types} from 'mobx-state-tree';
import {AuctionType, compareAuctionsByDate} from '../../types/auction/Auction';
import {getEndIndex, getStartIndex} from '../../utils/paginationUtils';
import {AuctionCardModel} from '../auctionCard/AuctionCardModel';
import {TransientEventTypeEnum} from '@joyrideautos/auction-core/src/types/events/transient';
import BaseViewModel from '../../BaseViewModel';
import {AuctionSeriesReferenceById} from '../../types/auction/Refs';
import {AnalyticsFavoritesEventEnum} from '@joyrideautos/ui-services/src/services/AnalyticsService';

export const AuctionSeriesViewModel = BaseViewModel.named('AuctionSeriesViewModel')
    .props({
        series: AuctionSeriesReferenceById,
        forUpcomingAuctions: types.boolean,
    })
    .volatile(() => ({
        currentPage: 1,
        disposers: [] as IDisposer[],
    }))
    .views((self) => {
        const {itemsPerPage} = getEnv<{regionId: string; itemsPerPage?: number}>(self);
        return {
            get allAuctions() {
                return self.auctionSeriesStore
                    .getAuctionsForSeries(self.series)
                    .filter(
                        ({ended, settings: {shouldHidePastAuctions}}) =>
                            (!ended && self.forUpcomingAuctions) ||
                            (ended && !self.forUpcomingAuctions && !shouldHidePastAuctions)
                    )
                    .slice()
                    .sort(compareAuctionsByDate(!self.forUpcomingAuctions));
            },
            get auctions() {
                if (itemsPerPage) {
                    const startIndex = getStartIndex(self.currentPage, itemsPerPage);
                    const endIndex = getEndIndex(self.currentPage, itemsPerPage, this.allAuctions.length);

                    return this.allAuctions.slice(startIndex, endIndex);
                }
                return this.allAuctions;
            },
            getAuctionCardModel(auction: AuctionType) {
                // TODO: destroy this
                return AuctionCardModel.create({auction: auction.auctionId}, {rootStore: self.rootStore});
            },
            get isFavorite() {
                return self.favoritesStore.isFavoriteAuctionSeries(self.series.key);
            },
            get isRestricted() {
                return self.series.isRestricted;
            },
            get sellers() {
                return this.allAuctions.map(({sellerIds}) => sellerIds).flat();
            },
            get accessGranted() {
                return self.biddersStatuses.hasAccessForSeries(self.series.key);
            },
            get isLive() {
                const lastAuction = this.allAuctions[0];
                const model =
                    lastAuction &&
                    self.liveAuctionStateStore.modelForAsync({
                        regionId: lastAuction.regionId,
                        auctionId: lastAuction.auctionId,
                    });
                return Boolean(model && model.isLive);
            },
            get liveAuctionTagText() {
                const lastAuction = this.allAuctions[0];
                return lastAuction?.liveAuctionTagText;
            },
            get totals() {
                return this.allAuctions.length;
            },
            get itemsPerPage() {
                return itemsPerPage;
            },
            get range() {
                if (itemsPerPage) {
                    const startIndex = getStartIndex(self.currentPage, itemsPerPage) + 1;
                    const endIndex = getEndIndex(self.currentPage, itemsPerPage, this.allAuctions.length);
                    return [startIndex, endIndex];
                }
                return [self.currentPage, self.currentPage];
            },
            get isSellerOrManager() {
                return self.sellerRoleStore.hasSellerRoleForAnyCompany(self.authUser?.uid, this.sellers);
            },
            get isRequestAccessButtonVisible(): boolean {
                return (
                    !self.authUserStore.isLoggedInUserAnonymous &&
                    self.forUpcomingAuctions &&
                    !this.isSellerOrManager &&
                    this.isRestricted &&
                    !this.accessGranted
                );
            },
        };
    })
    .actions((self) => {
        return {
            toggleFavorite: flow(function* () {
                if (self.series) {
                    const result = yield self.favoritesStore.toggleFavoriteAuctionSeries(self.series);
                    const eventType = result
                        ? TransientEventTypeEnum.FAVORITE_AUCTION_SERIES_ADDED
                        : TransientEventTypeEnum.FAVORITE_AUCTION_SERIES_REMOVED;
                    self.eventsStore.addTransientEvents({type: eventType, params: {itemTitle: self.series.title}});
                    // Firebase Analytics
                    const eventName = result
                        ? AnalyticsFavoritesEventEnum.ADD_TO_WISHLIST
                        : AnalyticsFavoritesEventEnum.REMOVE_FROM_WISHLIST;
                    yield self.rootStore.analyticsService.logFavoritesEvent(
                        eventName,
                        'auction series',
                        self.series.id
                    );
                }
            }),
            // TODO (Future): use /common/src/hocs/withOffsetPagination.tsx -> OffsetPaginationModel instead
            setCurrentPage(page: number) {
                self.currentPage = page;
            },
            afterCreate() {
                self.disposers.push(
                    self.biddersStatuses.subscribe(
                        self.series.key,
                        self.sellers.map((sellerId) => sellerId)
                    )
                );
            },
            beforeDestroy() {
                self.disposers.forEach((d) => d());
            },
        };
    });

export interface AuctionSeriesViewModelType extends Instance<typeof AuctionSeriesViewModel> {}
